import type { ReactNode } from "react";

import { MuiCard } from "@/components/ui/mui-card";
import { MuiDivider } from "@/components/ui/mui-divider";
import type { CatalogListItem } from "@/lib/catalog-item";
import { cn } from "@/lib/utils";
import { ItemEiLogo, ItemEiTierChips, ItemStatusOverlay } from "./item-badges";
import { ItemFields } from "./item-fields";
import { ItemHeading } from "./item-heading";
import { ItemHeroImage } from "./item-image";
import { type ItemActionsProps } from "./item-actions-menu";
import { type ItemSelectionProps } from "./item-select-checkbox";
import { ItemCornerControls } from "./item-corner-controls";
import { ItemSearchCardFace, type ItemSearchCardProps } from "./item-search-card-face";
import type { ItemVariantPreviewState } from "./item-variant-preview";

// One item as a card — the list's default view, and the only one that names the
// item's EI tiers.
//
// The arrangement is `item-card.tsx`'s, block for block: the status badge over
// the top-left corner, then a padded column holding a *centred, square* image
// with the EI tier chips under it, a horizontal rule, and then the text block —
// manufacturer, the name as the link, and the fields the item has.
//
// Two of those are corrections to this port's first pass, and both changed how
// the card reads at a glance:
//
//   - **The image is a centred 128px square, not a full-bleed banner.** The
//     donor draws `ItemImageBox size='medium'` (128x128) inside the card's own
//     16px padding, so every card shows a small picture floating over white
//     with air around it. A cropped edge-to-edge strip is a different object.
//   - **A rule separates the picture from the words** (`<Divider horizontal>`),
//     rather than the image carrying a bottom border.
//
// The batch-mode checkbox sits in the top-right corner while batch mode is on,
// and the per-card 3-dot actions menu takes that same corner when batch mode is
// off — the donor's own placement, and one corner with one control in it rather
// than two side by side (`{selectable && <tick/>}` / `{!selectable &&
// showItemActions && <menu/>}`).
//
// **Neither control asks about the other**, and that is the page's doing: it
// builds one bundle or the other, never both, so the exclusivity is a fact about
// the props rather than a condition repeated in two components (#457).
//
// The EI programme logo modsy overlays on the image is `ItemEiLogo`, below.
//
// **The variant strip arrives as a node, not as tile props** (#254). The card is
// the donor's `displayItem` consumer and nothing more: it is handed the item to
// draw and, if there is one, the strip to draw under the picture. The selection
// state and the lazy read of a sibling's details live in the *grid*, one host per
// card (`item-card-grid.tsx`), because both this component and the list row want
// the same four values around different markup — and because a card that owned a
// query could not be rendered in a test without one.

interface ItemCardProps extends ItemSelectionProps, ItemActionsProps, ItemSearchCardProps {
  /**
   * The item to draw — the **displayed** one, which is a variant of `ownItem`
   * whenever a tile is picked.
   *
   * Nearly the whole card follows it: the status badge, the picture, the EI mark
   * and tier chips, the manufacturer, the name *and the link under it*, and every
   * field.
   */
  item: CatalogListItem;
  /**
   * The card's own item, when it differs from the one on show.
   *
   * Only the card's own controls use it, and that is the donor being deliberate:
   * "the admin actions menu ... always operates on this card's item rather than
   * the variant currently being previewed" (`item-card.tsx:52-54`). The selection
   * checkbox is the same — a batch operation applies to the item the row *is*,
   * not to whichever sibling someone happened to preview. Note this is the
   * opposite of the details page, where the action menu does follow the variant.
   */
  ownItem?: CatalogListItem;
  /** The variant tile strip, or nothing for an ungrouped item. */
  variantTiles?: ReactNode;
  /**
   * The variant preview itself, for the Item Search island's face, which draws
   * its own 28px strip rather than the catalog's `variantTiles` and keys its
   * selected border on the tile pressed. Ignored without `search`.
   */
  variantPreview?: ItemVariantPreviewState;
  className?: string;
}

export function ItemCard({
  item,
  ownItem = item,
  variantTiles,
  className,
  selection,
  actions,
  search,
  variantPreview,
}: ItemCardProps) {
  // The Item Search island's face (#798) — a different donor card, drawn only
  // for a host that hands a search bundle. The catalog page never does, so
  // nothing below this line changes for it.
  if (search) {
    return (
      <ItemSearchCardFace
        item={item}
        ownItem={ownItem}
        search={search}
        preview={variantPreview}
        className={className}
      />
    );
  }

  return (
    // `<Card variant='outlined' sx={{ height: '100%', position: 'relative' }}>`
    // (`item-card.tsx:127`).
    <MuiCard data-testid="item-card" className={cn("relative h-full", className)}>
      <ItemStatusOverlay item={item} />
      <ItemCornerControls ownItem={ownItem} selection={selection} actions={actions} />
      <div className="flex flex-col gap-4 p-4">
        {/* Centred, and stretched to the card's width so the tier chips under
            the picture centre against the card rather than against the 128px
            image. */}
        {/* `relative` is the positioning context the EI mark hangs off — the
            donor's own arrangement, where the mark overlaps the lower-left of
            the picture rather than sitting beside it. */}
        <div className="relative flex flex-col items-center gap-2 self-stretch">
          {/* The donor's `size='medium'` `borderWidth={0}` box, handed the
              *displayed* item: the donor's `displayItem` feeds the picture and
              the details together (`item-card.tsx:71-89`), so a card showing a
              variant shows that variant's picture without this file knowing the
              gesture exists. */}
          {/* A plain block box around the picture, which is the donor's
              (`item-card.tsx:200-206`) and is load-bearing rather than
              decorative: `ItemImageBox` is `inline-flex`, so inside a block it
              sits on its line's baseline and the line box runs 5.5px past it
              (measured on the donor — a 128px box in a 133.5px wrapper). As a
              direct child of the flex column above, its display was blockified
              and that gap vanished, riding everything below the picture up by
              the same 5.5px. */}
          <div className="relative">
            <ItemHeroImage item={item} />
          </div>
          <ItemEiLogo item={item} />
          {/* Between the EI mark and the tier chips, which is the donor's order
              (`item-card.tsx:202-215`) — the strip sits under the picture and
              above the programme chips, not below both. */}
          {variantTiles}
          <ItemEiTierChips item={item} />
        </div>
        {/* `<Divider orientation='horizontal' flexItem />` (`item-card.tsx:218`).
            The shared port, not the raw primitive: `MuiDivider` carries MUI's
            translucent `palette.divider` and the `flexItem` behaviour the
            donor's rules rely on. */}
        <MuiDivider />
        <div className="flex min-w-0 flex-col items-start gap-1">
          <ItemHeading item={item} />
          <ItemFields item={item} className="flex w-full flex-col gap-1" />
        </div>
      </div>
    </MuiCard>
  );
}
